// OpenAI (ChatGPT Ads) measurement pixel. See components/analytics/OpenAIPixel.tsx.

const SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

type Oaiq = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    oaiq?: Oaiq;
  }
}

export function loadOpenAIPixel(pixelId: string): void {
  if (window.oaiq) {
    window.oaiq("consent", true);
    return;
  }
  const q: Oaiq = (...args: unknown[]) => {
    q.q!.push(args);
  };
  q.q = [];
  window.oaiq = q;
  const js = document.createElement("script");
  js.async = true;
  js.src = SDK_URL;
  document.head.appendChild(js);
  window.oaiq("consent", true);
  window.oaiq("init", { pixelId });
}

/** Phone in the shape the SDK hashes: country code + number, "+46701234567". */
function toE164(raw: string): string | undefined {
  const digits = raw.replace(/[\s().-]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("00")) return "+" + digits.slice(2);
  if (digits.startsWith("0")) return "+46" + digits.slice(1);
  return undefined;
}

export type OpenAIPurchaseUser = { email?: string; phone?: string };

/**
 * Sends a ChatGPT Ads purchase. No-op until the pixel is loaded (needs
 * marketing consent). The customer's email and phone go along as user data
 * (the SDK hashes them before sending), so OpenAI can match the booking to
 * the ad click even without the click-id cookie. The server sends the same
 * booking with the same id (lib/analytics/openai-capi.ts); OpenAI counts one.
 */
export function trackOpenAIPurchase(
  bookingId: string,
  valueSek: number | undefined,
  user: OpenAIPurchaseUser = {},
): void {
  if (typeof window === "undefined" || !window.oaiq) return;
  const pixelId = process.env.NEXT_PUBLIC_OPENAI_PIXEL_ID;
  const email = user.email?.trim().toLowerCase();
  const phone = user.phone ? toE164(user.phone) : undefined;
  if (pixelId && (email || phone)) {
    window.oaiq("init", {
      pixelId,
      user: {
        ...(email ? { email_sha256: email } : {}),
        ...(phone ? { phone_number_sha256: phone } : {}),
      },
    });
  }
  window.oaiq(
    "measure",
    "order_created",
    {
      type: "contents",
      // Minor units: öre.
      ...(valueSek !== undefined ? { amount: Math.round(valueSek * 100), currency: "SEK" } : {}),
    },
    // Same id as GA4's transaction_id and the server-side send, so OpenAI dedupes.
    { event_id: bookingId },
  );
}
